// SMSF Export Engine - PDF generation and reporting functionality
// Provides comprehensive export capabilities for all SMSF calculators

import React from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { FinancialConfig, calculateDivision293Detail, calculateDivision296Tax } from './FinancialConfig.js';

export const SMSFExportEngine = {
    // Generate HTML for PDF conversion
    generateReportHTML(reportType, data) {
        const timestamp = new Date().toLocaleString();
        const financialYear = this.getCurrentFinancialYear();
        
        const baseStyles = `
            <style>
                body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
                h1 { color: #1e40af; border-bottom: 3px solid #1e40af; padding-bottom: 10px; }
                h2 { color: #2563eb; margin-top: 30px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
                h3 { color: #3b82f6; margin-top: 20px; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th { background-color: #eff6ff; padding: 12px; text-align: left; border: 1px solid #ddd; }
                td { padding: 10px; border: 1px solid #ddd; }
                tr:nth-child(even) { background-color: #f9fafb; }
                .header { text-align: center; margin-bottom: 30px; }
                .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #ddd; font-size: 12px; color: #666; }
                .metric { display: inline-block; margin: 10px 20px; }
                .metric-value { font-size: 24px; font-weight: bold; color: #1e40af; }
                .metric-label { font-size: 14px; color: #666; }
                .warning { background-color: #fef3c7; padding: 15px; border-left: 4px solid #f59e0b; margin: 15px 0; }
                .error { background-color: #fee2e2; padding: 15px; border-left: 4px solid #ef4444; margin: 15px 0; }
                .success { background-color: #d1fae5; padding: 15px; border-left: 4px solid #10b981; margin: 15px 0; }
                .info { background-color: #dbeafe; padding: 15px; border-left: 4px solid #3b82f6; margin: 15px 0; }
                .disclaimer { background-color: #f3f4f6; padding: 15px; margin: 20px 0; font-size: 12px; }
            </style>
        `;

        let content = '';

        switch (reportType) {
            case 'comprehensive':
                content = this.generateComprehensiveReport(data);
                break;
            case 'fee_analysis':
                content = this.generateFeeAnalysisReport(data);
                break;
            case 'compliance':
                content = this.generateComplianceReport(data);
                break;
            case 'lrba':
                content = this.generateLRBAReport(data);
                break;
            case 'division293':
                content = this.generateDivision293Report(data);
                break;
            case 'division296':
                content = this.generateDivision296Report(data);
                break;
            case 'pension':
                content = this.generatePensionReport(data);
                break;
            case 'collectibles':
                content = this.generateCollectiblesReport(data);
                break;
            case 'tax_harvesting':
                content = this.generateTaxHarvestingReport(data);
                break;
            case 'ato_annual':
                content = this.generateATOAnnualReport(data);
                break;
            default:
                content = '<p>Report type not found</p>';
        }

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${reportType.toUpperCase()} Report - ${financialYear}</title>
                ${baseStyles}
            </head>
            <body>
                <div class="header">
                    <h1>SMSF Calculator Report</h1>
                    <p><strong>Report Type:</strong> ${this.formatReportType(reportType)}</p>
                    <p><strong>Financial Year:</strong> ${financialYear}</p>
                    <p><strong>Generated:</strong> ${timestamp}</p>
                    ${data.fundName ? `<p><strong>Fund:</strong> ${data.fundName}</p>` : ''}
                </div>
                
                ${content}

                ${this.generateTransparencySection(reportType, data)}
                
                <div class="footer">
                    <p><strong>Disclaimer:</strong> This report is for informational purposes only and does not constitute financial, tax, or legal advice. 
                    Consult with qualified professionals before making any decisions based on this information.</p>
                    <p><strong>Generated by:</strong> SMSF Calculator Suite | <strong>Date:</strong> ${timestamp}</p>
                </div>
            </body>
            </html>
        `;
    },

    generateTransparencySection(reportType, data = {}) {
        const rows = data.projections || data.yearlyData || data.tableData || [];
        const warnings = data.warnings || data.issues || [];
        return `
            <h2>Calculation Transparency</h2>
            <div class="info">
                <p><strong>Everything considered:</strong> This ${this.formatReportType(reportType)} report can use the fund, member, balance, Total Super Balance, contribution, property, pension, tax, borrowing, income, compliance, investment, insurance, estate, and reporting fields relevant to the selected SMSF module.</p>
                <p><strong>Rules applied:</strong> The report applies the configured SMSF thresholds, caps, contribution limits, tax rates, age limits, borrowing settings, eligibility checks, compliance flags, and recommendations used by that calculator module.</p>
                <p><strong>Trace available:</strong> ${rows.length > 0 ? `${rows.length} projection/detail rows are included or available from the calculator output.` : 'This report is based on point-in-time calculations rather than a yearly projection.'}</p>
                <p><strong>Warnings:</strong> ${warnings.length > 0 ? `${warnings.length} warning or issue flag(s) were generated.` : 'No warning flags were generated by this calculator output.'}</p>
            </div>
        `;
    },

    // Generate comprehensive SMSF report
    generateComprehensiveReport(data) {
        return `
            <h2>Fund Overview</h2>
            <table>
                <tr><td><strong>Fund Name:</strong></td><td>${data.fundName || 'N/A'}</td></tr>
                <tr><td><strong>ABN:</strong></td><td>${data.abn || 'N/A'}</td></tr>
                <tr><td><strong>Fund Balance:</strong></td><td>$${(data.balance || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Number of Members:</strong></td><td>${data.numberOfMembers || 0}</td></tr>
                <tr><td><strong>Trustee Type:</strong></td><td>${data.trusteeType || 'N/A'}</td></tr>
            </table>

            <h2>Contributions Summary</h2>
            <table>
                <thead>
                    <tr>
                        <th>Contribution Type</th>
                        <th>Amount</th>
                        <th>Cap</th>
                        <th>Remaining</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Concessional</td>
                        <td>$${(data.concessionalUsed || 0).toLocaleString()}</td>
                        <td>$${SMSF_RATES_CONFIG.contributionCaps.concessional.limit.toLocaleString()}</td>
                        <td>$${(SMSF_RATES_CONFIG.contributionCaps.concessional.limit - (data.concessionalUsed || 0)).toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td>Non-Concessional</td>
                        <td>$${(data.nonConcessionalUsed || 0).toLocaleString()}</td>
                        <td>$${SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit.toLocaleString()}</td>
                        <td>$${(SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit - (data.nonConcessionalUsed || 0)).toLocaleString()}</td>
                    </tr>
                </tbody>
            </table>

            <h2>Fee Summary</h2>
            <table>
                <thead>
                    <tr><th>Fee Type</th><th>Annual Amount</th></tr>
                </thead>
                <tbody>
                    <tr><td>Audit Fees</td><td>$${(data.auditFees || 0).toLocaleString()}</td></tr>
                    <tr><td>Administration Fees</td><td>$${(data.adminFees || 0).toLocaleString()}</td></tr>
                    <tr><td>Accounting Fees</td><td>$${(data.accountingFees || 0).toLocaleString()}</td></tr>
                    <tr><td><strong>Total Annual Fees</strong></td><td><strong>$${((data.auditFees || 0) + (data.adminFees || 0) + (data.accountingFees || 0)).toLocaleString()}</strong></td></tr>
                </tbody>
            </table>

            ${data.hasLRBA ? `
                <h2>LRBA Details</h2>
                <table>
                    <tr><td><strong>Property Value:</strong></td><td>$${(data.propertyValue || 0).toLocaleString()}</td></tr>
                    <tr><td><strong>Loan Amount:</strong></td><td>$${(data.propertyLoan || 0).toLocaleString()}</td></tr>
                    <tr><td><strong>LVR:</strong></td><td>${((data.propertyLoan / data.propertyValue) * 100).toFixed(1)}%</td></tr>
                    <tr><td><strong>Interest Rate:</strong></td><td>${data.propertyInterestRate || 0}%</td></tr>
                </table>
            ` : ''}
        `;
    },

    // Generate fee analysis report
    generateFeeAnalysisReport(data) {
        const totalFees = (data.auditFees || 0) + (data.adminFees || 0) + (data.accountingFees || 0);
        const feePercent = ((totalFees / (data.balance || 1)) * 100).toFixed(2);

        return `
            <h2>Fee Analysis Report</h2>
            
            <div class="metric">
                <div class="metric-value">$${totalFees.toLocaleString()}</div>
                <div class="metric-label">Total Annual Fees</div>
            </div>
            <div class="metric">
                <div class="metric-value">${feePercent}%</div>
                <div class="metric-label">Fee as % of Balance</div>
            </div>

            <h3>Detailed Fee Breakdown</h3>
            <table>
                <thead>
                    <tr>
                        <th>Fee Type</th>
                        <th>Annual Amount</th>
                        <th>% of Total Fees</th>
                        <th>% of Fund Balance</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Audit Fees</td>
                        <td>$${(data.auditFees || 0).toLocaleString()}</td>
                        <td>${((data.auditFees / totalFees) * 100).toFixed(1)}%</td>
                        <td>${((data.auditFees / data.balance) * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                        <td>Administration Fees</td>
                        <td>$${(data.adminFees || 0).toLocaleString()}</td>
                        <td>${((data.adminFees / totalFees) * 100).toFixed(1)}%</td>
                        <td>${((data.adminFees / data.balance) * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                        <td>Accounting Fees</td>
                        <td>$${(data.accountingFees || 0).toLocaleString()}</td>
                        <td>${((data.accountingFees / totalFees) * 100).toFixed(1)}%</td>
                        <td>${((data.accountingFees / data.balance) * 100).toFixed(2)}%</td>
                    </tr>
                    <tr style="background-color: #eff6ff;">
                        <td><strong>Total</strong></td>
                        <td><strong>$${totalFees.toLocaleString()}</strong></td>
                        <td><strong>100.0%</strong></td>
                        <td><strong>${feePercent}%</strong></td>
                    </tr>
                </tbody>
            </table>

            <h3>Industry Comparison</h3>
            <div class="${feePercent > 2 ? 'warning' : 'success'}">
                <p><strong>Fee Assessment:</strong> ${
                    feePercent < 1 ? 'Excellent - Below industry average' :
                    feePercent < 1.5 ? 'Good - Within typical range' :
                    feePercent < 2 ? 'Average - Typical for SMSF' :
                    'High - Consider reviewing fee arrangements'
                }</p>
                <p>Industry Average: 1.0-2.0% of fund balance</p>
            </div>
        `;
    },

    // Generate compliance report
    generateComplianceReport(data) {
        const timestamp = new Date().toLocaleString();
        const concessionalRemaining = SMSF_RATES_CONFIG.contributionCaps.concessional.limit - (data.concessionalUsed || 0);
        const nonConcessionalRemaining = SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit - (data.nonConcessionalUsed || 0);
        const tbcRemaining = SMSF_RATES_CONFIG.contributionCaps.totalSuperBalanceThreshold - ((data.yourTbcUsed || 0) + (data.partnerTbcUsed || 0));

        return `
            <h2>SMSF Compliance Report</h2>

            <h3>Contribution Compliance</h3>
            <table>
                <thead>
                    <tr>
                        <th>Contribution Type</th>
                        <th>Used</th>
                        <th>Cap</th>
                        <th>Remaining</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Concessional Contributions</td>
                        <td>$${(data.concessionalUsed || 0).toLocaleString()}</td>
                        <td>$${SMSF_RATES_CONFIG.contributionCaps.concessional.limit.toLocaleString()}</td>
                        <td>$${concessionalRemaining.toLocaleString()}</td>
                        <td>${concessionalRemaining >= 0 ? '✓ Compliant' : '✗ Exceeded'}</td>
                    </tr>
                    <tr>
                        <td>Non-Concessional Contributions</td>
                        <td>$${(data.nonConcessionalUsed || 0).toLocaleString()}</td>
                        <td>$${SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit.toLocaleString()}</td>
                        <td>$${nonConcessionalRemaining.toLocaleString()}</td>
                        <td>${nonConcessionalRemaining >= 0 ? '✓ Compliant' : '✗ Exceeded'}</td>
                    </tr>
                    <tr>
                        <td>Transfer Balance Cap</td>
                        <td>$${((data.yourTbcUsed || 0) + (data.partnerTbcUsed || 0)).toLocaleString()}</td>
                        <td>$${SMSF_RATES_CONFIG.transferBalanceCap.generalTBC.toLocaleString()}</td>
                        <td>$${tbcRemaining.toLocaleString()}</td>
                        <td>${tbcRemaining >= 0 ? '✓ Compliant' : '✗ Exceeded'}</td>
                    </tr>
                </tbody>
            </table>

            ${data.inHouseAssets > 0 ? `
                <h3>In-House Assets</h3>
                <table>
                    <tr>
                        <td><strong>In-House Assets:</strong></td>
                        <td>$${(data.inHouseAssets || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td><strong>Total Fund Assets:</strong></td>
                        <td>$${(data.balance || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td><strong>In-House Percentage:</strong></td>
                        <td>${((data.inHouseAssets / data.balance) * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                        <td><strong>Compliance Status:</strong></td>
                        <td>${(data.inHouseAssets / data.balance) * 100 <= 5 ? '✓ Compliant (Under 5%)' : '✗ Exceeds 5% Limit'}</td>
                    </tr>
                </table>
            ` : ''}

            <div class="disclaimer">
                <p><strong>Note:</strong> This compliance report is based on information provided and current regulatory requirements as of ${timestamp}. 
                Regulations may change. Consult with your SMSF auditor or accountant for formal compliance certification.</p>
            </div>
        `;
    },

    // Generate LRBA report
    generateLRBAReport(data) {
        const lvr = (data.loanAmount / data.propertyValue) * 100;
        const maxLvrPct = (FinancialConfig.SMSF?.BORROWING?.LRBA?.MAXIMUM_LOAN_TO_VALUE ?? 0.70) * 100;
        const monthlyRate = data.interestRate / 100 / 12;
        const totalPayments = data.loanTerm * 12;
        const monthlyPayment = data.loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
        const annualPayment = monthlyPayment * 12;

        return `
            <h2>LRBA Analysis Report</h2>

            <h3>Property Details</h3>
            <table>
                <tr><td><strong>Property Value:</strong></td><td>$${(data.propertyValue || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Property Type:</strong></td><td>${data.propertyType || 'N/A'}</td></tr>
                <tr><td><strong>Rental Income (Annual):</strong></td><td>$${(data.rentalIncome || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Rental Yield:</strong></td><td>${((data.rentalIncome / data.propertyValue) * 100).toFixed(2)}%</td></tr>
            </table>

            <h3>Loan Structure</h3>
            <table>
                <tr><td><strong>Loan Amount:</strong></td><td>$${(data.loanAmount || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Interest Rate:</strong></td><td>${data.interestRate || 0}%</td></tr>
                <tr><td><strong>Loan Term:</strong></td><td>${data.loanTerm || 0} years</td></tr>
                <tr><td><strong>LVR:</strong></td><td>${lvr.toFixed(1)}%</td></tr>
                <tr><td><strong>Monthly Repayment:</strong></td><td>$${monthlyPayment.toLocaleString()}</td></tr>
                <tr><td><strong>Annual Repayment:</strong></td><td>$${annualPayment.toLocaleString()}</td></tr>
            </table>

            <h3>Cash Flow Analysis</h3>
            <table>
                <tr><td><strong>Annual Rental Income:</strong></td><td>$${(data.rentalIncome || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Annual Expenses:</strong></td><td>-$${(data.propertyExpenses || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Annual Loan Repayment:</strong></td><td>-$${annualPayment.toLocaleString()}</td></tr>
                <tr style="background-color: #eff6ff;">
                    <td><strong>Net Annual Cash Flow:</strong></td>
                    <td><strong>$${(data.rentalIncome - data.propertyExpenses - annualPayment).toLocaleString()}</strong></td>
                </tr>
            </table>

            <div class="${lvr > maxLvrPct ? 'error' : 'success'}">
                <p><strong>Compliance Status:</strong> ${lvr <= maxLvrPct ? `✓ LVR compliant (≤${maxLvrPct}%)` : `✗ LVR exceeds ${maxLvrPct}% maximum`}</p>
            </div>
        `;
    },

    // Generate Division 293 report
    generateDivision293Report(data) {
        const incomeForSurcharge = data.assessableIncome + data.reportableFringeBenefits + data.reportableEmployerSuper - data.netInvestmentLoss;
        const detail = calculateDivision293Detail(incomeForSurcharge, data.concessionalContributions || 0);
        const threshold = FinancialConfig.DIVISION_293.INCOME_THRESHOLD;
        const div293Tax = detail.div293Tax;

        return `
            <h2>Division 293 Tax Assessment</h2>

            <h3>Income Calculation</h3>
            <table>
                <tr><td>Assessable Income</td><td>$${(data.assessableIncome || 0).toLocaleString()}</td></tr>
                <tr><td>+ Reportable Fringe Benefits</td><td>$${(data.reportableFringeBenefits || 0).toLocaleString()}</td></tr>
                <tr><td>+ Reportable Employer Super</td><td>$${(data.reportableEmployerSuper || 0).toLocaleString()}</td></tr>
                <tr><td>- Net Investment Loss</td><td>$${(data.netInvestmentLoss || 0).toLocaleString()}</td></tr>
                <tr style="background-color: #eff6ff;">
                    <td><strong>Income for Surcharge Purposes</strong></td>
                    <td><strong>$${incomeForSurcharge.toLocaleString()}</strong></td>
                </tr>
            </table>

            <h3>Division 293 Tax Calculation</h3>
            <table>
                <tr><td>Income for Surcharge Purposes</td><td>$${incomeForSurcharge.toLocaleString()}</td></tr>
                <tr><td>- Division 293 Threshold</td><td>$${threshold.toLocaleString()}</td></tr>
                <tr><td>Excess Income</td><td>$${detail.excessIncome.toLocaleString()}</td></tr>
                <tr><td>Concessional Contributions</td><td>$${(data.concessionalContributions || 0).toLocaleString()}</td></tr>
                <tr><td>Taxable Amount (Lesser)</td><td>$${detail.taxableAmount.toLocaleString()}</td></tr>
                <tr style="background-color: #fee2e2;">
                    <td><strong>Division 293 Tax Payable (${(FinancialConfig.DIVISION_293.TAX_RATE * 100)}%)</strong></td>
                    <td><strong>$${div293Tax.toLocaleString()}</strong></td>
                </tr>
            </table>

            <div class="${div293Tax > 0 ? 'warning' : 'success'}">
                <p><strong>Tax Liability:</strong> ${
                    div293Tax === 0 ? 'No Division 293 tax applies - income below threshold' :
                    `Division 293 tax of $${div293Tax.toLocaleString()} applies`
                }</p>
            </div>
        `;
    },

    generateDivision296Report(data) {
        const result = calculateDivision296Tax(
            data.tsbStart || 0,
            data.tsbEnd || 0,
            data.contributions || 0,
            data.benefitsPaid || 0,
            {
                division296Earnings: data.division296Earnings ?? data.superEarnings ?? null,
                useTsbProxy: !!data.useTsbProxy,
                financialYear: data.financialYear || '2026-27',
                earningsSource: data.earningsSource || 'reported',
                planningMode: true,
            }
        );

        return `
            <h2>Division 296 Tax Assessment (Planning)</h2>
            <table>
                <tr><td>TSB at start of FY</td><td>$${(data.tsbStart || 0).toLocaleString()}</td></tr>
                <tr><td>TSB at end of FY</td><td>$${(data.tsbEnd || 0).toLocaleString()}</td></tr>
                <tr><td>Division 296 fund earnings</td><td>$${(result.superEarnings || 0).toLocaleString()}</td></tr>
                <tr><td>TSB for proportion test</td><td>$${(result.tsbForProportion || 0).toLocaleString()}</td></tr>
                <tr><td>Proportion above $${(result.threshold || 0).toLocaleString()}</td><td>${((result.proportion || 0) * 100).toFixed(2)}%</td></tr>
                <tr style="background-color: #fee2e2;">
                    <td><strong>Division 296 Tax Payable</strong></td>
                    <td><strong>$${(result.taxAmount || 0).toLocaleString()}</strong></td>
                </tr>
            </table>
            <div class="info">
                <p>Planning estimate only. Member-level attribution and multi-fund TSB aggregation are not modelled.</p>
            </div>
        `;
    },

    // Generate pension report
    generatePensionReport(data) {
        const minDrawdown = this.calculateMinDrawdown(data.age || 65);
        const minPension = data.balance * (minDrawdown / 100);

        return `
            <h2>Pension Analysis Report</h2>

            <h3>Member Details</h3>
            <table>
                <tr><td><strong>Current Age:</strong></td><td>${data.age || 65}</td></tr>
                <tr><td><strong>Life Expectancy:</strong></td><td>${data.lifeExpectancy || 90}</td></tr>
                <tr><td><strong>Years in Retirement:</strong></td><td>${(data.lifeExpectancy || 90) - (data.age || 65)}</td></tr>
            </table>

            <h3>Pension Calculation</h3>
            <table>
                <tr><td><strong>Current Balance:</strong></td><td>$${(data.balance || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Minimum Drawdown %:</strong></td><td>${minDrawdown}%</td></tr>
                <tr><td><strong>Minimum Annual Pension:</strong></td><td>$${minPension.toLocaleString()}</td></tr>
                <tr><td><strong>Desired Annual Income:</strong></td><td>$${(data.desiredIncome || 0).toLocaleString()}</td></tr>
            </table>

            <h3>Tax Benefits</h3>
            <table>
                <tr><td>Tax on Investment Income</td><td>0% (vs 15% in accumulation)</td></tr>
                <tr><td>Tax on Capital Gains</td><td>0% (vs 10% in accumulation)</td></tr>
                <tr><td>Annual Tax Savings (Estimated)</td><td>$${((data.desiredIncome || 0) * 0.15).toLocaleString()}</td></tr>
            </table>
        `;
    },

    // Generate collectibles report
    generateCollectiblesReport(data) {
        return `
            <h2>Collectibles Inventory Report</h2>

            <h3>Portfolio Summary</h3>
            <div class="metric">
                <div class="metric-value">${data.count || 0}</div>
                <div class="metric-label">Total Collectibles</div>
            </div>
            <div class="metric">
                <div class="metric-value">$${(data.totalValue || 0).toLocaleString()}</div>
                <div class="metric-label">Total Value</div>
            </div>
            <div class="metric">
                <div class="metric-value">${(data.percentOfAssets || 0).toFixed(1)}%</div>
                <div class="metric-label">% of SMSF Assets</div>
            </div>

            <div class="${data.percentOfAssets > 5 ? 'error' : 'success'}">
                <p><strong>Compliance Status:</strong> ${
                    data.percentOfAssets <= 5 ? '✓ Under 5% regulatory limit' : '✗ Exceeds 5% limit - action required'
                }</p>
            </div>

            <h3>Collectibles Inventory</h3>
            <table>
                <thead>
                    <tr>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Purchase Price</th>
                        <th>Current Value</th>
                        <th>Gain/Loss</th>
                        <th>Insured</th>
                    </tr>
                </thead>
                <tbody>
                    ${(data.items || []).map(item => `
                        <tr>
                            <td>${item.description}</td>
                            <td>${item.type}</td>
                            <td>$${item.purchasePrice.toLocaleString()}</td>
                            <td>$${item.currentValue.toLocaleString()}</td>
                            <td>${item.currentValue >= item.purchasePrice ? '+' : ''}$${(item.currentValue - item.purchasePrice).toLocaleString()}</td>
                            <td>${item.insured ? '✓ Yes' : '✗ No'}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    },

    // Generate tax harvesting report
    generateTaxHarvestingReport(data) {
        return `
            <h2>Tax Loss Harvesting Analysis</h2>

            <h3>Portfolio Tax Position</h3>
            <div class="metric">
                <div class="metric-value">$${(data.unrealizedGains || 0).toLocaleString()}</div>
                <div class="metric-label">Unrealized Gains</div>
            </div>
            <div class="metric">
                <div class="metric-value">$${(data.unrealizedLosses || 0).toLocaleString()}</div>
                <div class="metric-label">Unrealized Losses</div>
            </div>
            <div class="metric">
                <div class="metric-value">$${(data.harvestingSavings || 0).toLocaleString()}</div>
                <div class="metric-label">Potential Tax Savings</div>
            </div>

            <h3>Harvesting Opportunities</h3>
            <table>
                <thead>
                    <tr>
                        <th>Holding</th>
                        <th>Action</th>
                        <th>Amount</th>
                        <th>Tax Impact</th>
                        <th>Priority</th>
                    </tr>
                </thead>
                <tbody>
                    ${(data.opportunities || []).map(opp => `
                        <tr>
                            <td>${opp.holding}</td>
                            <td>${opp.action}</td>
                            <td>$${(opp.loss || opp.gain || 0).toLocaleString()}</td>
                            <td>$${(opp.taxSaving || opp.potentialSaving || 0).toLocaleString()}</td>
                            <td>${opp.priority}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    },

    // Generate ATO annual report
    generateATOAnnualReport(data) {
        const financialYear = this.getCurrentFinancialYear();
        
        return `
            <h2>ATO Annual Return Summary</h2>
            <p><strong>Financial Year:</strong> ${financialYear}</p>

            <h3>Fund Details</h3>
            <table>
                <tr><td><strong>Fund Name:</strong></td><td>${data.fundName || 'N/A'}</td></tr>
                <tr><td><strong>ABN:</strong></td><td>${data.abn || 'N/A'}</td></tr>
                <tr><td><strong>TFN:</strong></td><td>${data.tfn || 'N/A'}</td></tr>
                <tr><td><strong>Trustee Type:</strong></td><td>${data.trusteeType || 'N/A'}</td></tr>
                <tr><td><strong>Number of Members:</strong></td><td>${data.numberOfMembers || 0}</td></tr>
            </table>

            <h3>Contributions Received</h3>
            <table>
                <thead>
                    <tr><th>Type</th><th>Amount</th></tr>
                </thead>
                <tbody>
                    <tr><td>Employer Contributions</td><td>$${(data.employerContributions || 0).toLocaleString()}</td></tr>
                    <tr><td>Member Contributions</td><td>$${(data.memberContributions || 0).toLocaleString()}</td></tr>
                    <tr><td>Total Concessional</td><td>$${(data.concessionalUsed || 0).toLocaleString()}</td></tr>
                    <tr><td>Non-Concessional</td><td>$${(data.nonConcessionalUsed || 0).toLocaleString()}</td></tr>
                </tbody>
            </table>

            <h3>Fund Income</h3>
            <table>
                <tr><td>Investment Income</td><td>$${(data.investmentIncome || 0).toLocaleString()}</td></tr>
                <tr><td>Rental Income</td><td>$${(data.rentalIncome || 0).toLocaleString()}</td></tr>
                <tr><td>Capital Gains</td><td>$${(data.capitalGains || 0).toLocaleString()}</td></tr>
            </table>

            <h3>Fund Expenses</h3>
            <table>
                <tr><td>Administration Fees</td><td>$${(data.adminFees || 0).toLocaleString()}</td></tr>
                <tr><td>Audit Fees</td><td>$${(data.auditFees || 0).toLocaleString()}</td></tr>
                <tr><td>Accounting Fees</td><td>$${(data.accountingFees || 0).toLocaleString()}</td></tr>
                <tr><td>Investment Expenses</td><td>$${(data.investmentExpenses || 0).toLocaleString()}</td></tr>
            </table>

            <h3>Asset Summary</h3>
            <table>
                <tr><td><strong>Opening Balance:</strong></td><td>$${(data.openingBalance || 0).toLocaleString()}</td></tr>
                <tr><td><strong>Closing Balance:</strong></td><td>$${(data.balance || 0).toLocaleString()}</td></tr>
            </table>

            <div class="info">
                <p><strong>ATO Lodgment:</strong> This summary provides key information for your annual SMSF return. 
                Ensure all figures are verified by your accountant before lodging with the ATO.</p>
                <p><strong>Due Date:</strong> Generally 28 February (or later with registered tax agent)</p>
            </div>
        `;
    },

    // Helper: Get current financial year
    getCurrentFinancialYear() {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1; // 0-indexed
        
        if (month >= 7) {
            return `${year}-${year + 1}`;
        } else {
            return `${year - 1}-${year}`;
        }
    },

    // Helper: Format report type
    formatReportType(type) {
        return type.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    },

    // Helper: Calculate minimum drawdown
    calculateMinDrawdown(age) {
        if (age < 65) return 4.0;
        if (age < 75) return 5.0;
        if (age < 80) return 6.0;
        if (age < 85) return 7.0;
        if (age < 90) return 9.0;
        if (age < 95) return 11.0;
        return 14.0;
    },

    // Export to PDF (browser print)
    exportToPDF(reportType, data, filename) {
        try {
            console.log('Starting PDF export for:', reportType);
            console.log('Data received:', data);
            
            const html = this.generateReportHTML(reportType, data);
            console.log('HTML generated, length:', html.length);
            
            // Try method 1: New window with popup
            console.log('Attempting to open new window...');
            const printWindow = window.open('', '_blank', 'width=1000,height=800,menubar=yes,toolbar=yes,location=yes');
            
            // Check if popup was blocked
            if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
                console.warn('Popup blocked, using fallback method');
                
                // Fallback: Create a blob and download as HTML
                const blob = new Blob([html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${filename}.html`;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                
                alert('✅ Report Downloaded!\n\n' +
                      'Your browser blocked popups, so the report was downloaded as an HTML file instead.\n\n' +
                      '📂 Check your Downloads folder for: ' + filename + '.html\n\n' +
                      'To view and print:\n' +
                      '1. Open the downloaded HTML file in your browser\n' +
                      '2. Press Ctrl+P (Windows) or Cmd+P (Mac)\n' +
                      '3. Select "Save as PDF" from the printer dropdown\n\n' +
                      'To enable popup reports, click the popup blocker icon in your address bar.');
                
                return true;
            }
            
            console.log('New window opened successfully');
            printWindow.document.write(html);
            printWindow.document.close();
            
            // Wait for content to load then print
            printWindow.onload = () => {
                console.log('Window loaded, triggering print dialog');
                setTimeout(() => {
                    printWindow.print();
                }, 250);
            };
            
            return true;
        } catch (error) {
            console.error('Report generation error:', error);
            console.error('Error stack:', error.stack);
            alert('❌ Failed to generate report: ' + error.message + '\n\nCheck the browser console (F12) for details.');
            return false;
        }
    },

    // Export to HTML file
    exportToHTML(reportType, data, filename) {
        const html = this.generateReportHTML(reportType, data);
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `${reportType}_report_${Date.now()}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    },

    // Export to JSON
    exportToJSON(data, filename) {
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `smsf_data_${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};

export default SMSFExportEngine;
